import {
  createSlice,
  PayloadAction,
  createListenerMiddleware,
} from "@reduxjs/toolkit";
import { SetEventoId } from "./eventos.slice";

// Constantes para localStorage
const EVENTO_MODE_KEY = "select-evento-mode";

// Sistema de modos baseado em configuração
export interface ModeConfig {
  id: string;
  name: string;
  featureFlag: string;
  loginProperty: string;
  displayName: string;
  order: number;
}

export const AVAILABLE_MODES: ModeConfig[] = [
  {
    id: "eventos",
    name: "eventos",
    featureFlag: "Eventos", // Sempre habilitado
    loginProperty: "Eventos", // Sempre true
    displayName: "Eventos",
    order: 1,
  },
  {
    id: "pequenos-grupos",
    name: "pequenos-grupos",
    featureFlag: "PequenosGrupos",
    loginProperty: "PequenosGrupos",
    displayName: "Pequenos Grupos",
    order: 2,
  },
  {
    id: "liderancas",
    name: "liderancas",
    featureFlag: "Liderancas",
    loginProperty: "Liderancas",
    displayName: "Lideranças",
    order: 3,
  },
  {
    id: "oracao",
    name: "oracao",
    featureFlag: "Oracao",
    loginProperty: "Oracao",
    displayName: "Oração",
    order: 4,
  },
  {
    id: "ensino",
    name: "ensino",
    featureFlag: "Ensino",
    loginProperty: "Ensino",
    displayName: "Ensino",
    order: 5,
  },
  {
    id: "infantil",
    name: "infantil",
    featureFlag: "Infantil",
    loginProperty: "Infantil",
    displayName: "Infantil",
    order: 6,
  },
  {
    id: "devocionais",
    name: "devocionais",
    featureFlag: "Devocionais",
    loginProperty: "Devocionais",
    displayName: "Devocional",
    order: 7,
  },
  {
    id: "igreja",
    name: "igreja",
    featureFlag: "Igrejas",
    loginProperty: "Igrejas",
    displayName: "Igreja",
    order: 8,
  },
  {
    id: "escala",
    name: "escala",
    featureFlag: "Igrejas",
    loginProperty: "Igrejas",
    displayName: "Escala",
    order: 9,
  },
  {
    id: "plus",
    name: "plus",
    featureFlag: "SysIgrejaPlus",
    loginProperty: "Plus",
    displayName: "SysIgreja Plus",
    order: 10,
  },
  {
    // #933 Questionários configuráveis: só Master (featureFlag/loginProperty não usados: ver useEventoMode)
    id: "questionarios",
    name: "questionarios",
    featureFlag: "Questionarios",
    loginProperty: "Questionarios",
    displayName: "Questionários",
    order: 11,
  },
];

export type EventoMode = (typeof AVAILABLE_MODES)[number]["id"];

// Função para obter o modo inicial do localStorage
const getInitialMode = (): EventoMode => {
  if (typeof window !== "undefined") {
    const savedMode = localStorage.getItem(EVENTO_MODE_KEY);
    // Migração: "igrejas" foi renomeado para "igreja"
    if (savedMode === "igrejas") {
      localStorage.setItem(EVENTO_MODE_KEY, "igreja");
      return "igreja";
    }
    return (savedMode as EventoMode) || "eventos";
  }
  return "eventos";
};

interface EventoModeState {
  eventoMode: EventoMode;
  availableModes: EventoMode[];
  modeConfigs: Record<EventoMode, boolean>; // Estado de cada modo
  hasLoadedModes: boolean; // Flag para indicar se os modos já foram carregados
}

const initialState: EventoModeState = {
  eventoMode: getInitialMode(),
  availableModes: ["eventos"], // Sempre disponível
  modeConfigs: AVAILABLE_MODES.reduce((acc, mode) => {
    acc[mode.id as EventoMode] = mode.id === "eventos"; // Apenas eventos habilitado por padrão
    return acc;
  }, {} as Record<EventoMode, boolean>),
  hasLoadedModes: false, // Começa como false
};

export const eventoModeSlice = createSlice({
  name: "eventoMode",
  initialState,
  reducers: {
    setEventoMode: (state, action: PayloadAction<EventoMode>) => {
      // Só permite definir se o modo estiver disponível
      if (state.availableModes.includes(action.payload)) {
        state.eventoMode = action.payload;

        if (typeof window !== "undefined") {
          localStorage.setItem(EVENTO_MODE_KEY, action.payload);
        }
      }
    },

    toggleEventoMode: (state) => {
      const currentIndex = state.availableModes.indexOf(state.eventoMode);
      const nextIndex = (currentIndex + 1) % state.availableModes.length;
      const nextMode = state.availableModes[nextIndex];

      state.eventoMode = nextMode;
      if (typeof window !== "undefined") {
        localStorage.setItem(EVENTO_MODE_KEY, nextMode);
      }
    },

    updateAvailableModes: (state, action: PayloadAction<EventoMode[]>) => {
      const newAvailableModes = action.payload;
      state.availableModes = newAvailableModes;

      const savedMode = getInitialMode();
      const savedIsAvailable = newAvailableModes.includes(savedMode);

      if (savedIsAvailable) {
        state.eventoMode = savedMode;
        state.hasLoadedModes = true;
        return;
      }

      if (!state.hasLoadedModes) {
        if (state.eventoMode === savedMode || newAvailableModes.includes(state.eventoMode)) {
          state.hasLoadedModes = true;
          return;
        }
        state.eventoMode = newAvailableModes[0] || "eventos";
        state.hasLoadedModes = true;
        return;
      }

      // Permissões async ainda podem incluir o modo salvo — não derrubar preferência do usuário
      if (state.eventoMode === savedMode) {
        return;
      }

      if (!newAvailableModes.includes(state.eventoMode)) {
        state.eventoMode = newAvailableModes[0] || "eventos";
      }
    },

    // Nova action para forçar a restauração do modo salvo
    restoreSavedMode: (state) => {
      const savedMode = getInitialMode();
      if (state.availableModes.includes(savedMode)) {
        state.eventoMode = savedMode;
      }
    },

    setHasLoadedModes: (state) => {
      state.hasLoadedModes = true;
    },

    updateModeConfig: (
      state,
      action: PayloadAction<{ mode: EventoMode; enabled: boolean }>
    ) => {
      const { mode, enabled } = action.payload;
      state.modeConfigs[mode] = enabled;
    },
  },
});

export const {
  setEventoMode,
  toggleEventoMode,
  updateAvailableModes,

  restoreSavedMode,
  updateModeConfig,
  setHasLoadedModes,
} = eventoModeSlice.actions;

// Selectors dinâmicos
export const selectEventoMode = (state: { eventoMode: EventoModeState }) =>
  state.eventoMode.eventoMode;

export const selectAvailableModes = (state: { eventoMode: EventoModeState }) =>
  state.eventoMode.availableModes;

export const selectModeConfigs = (state: { eventoMode: EventoModeState }) =>
  state.eventoMode.modeConfigs;

export const selectHasLoadedModes = (state: { eventoMode: EventoModeState }) =>
  state.eventoMode.hasLoadedModes;

// Selectors específicos para cada modo (gerados dinamicamente)
export const createModeSelectors = () => {
  const selectors: Record<
    string,
    (state: { eventoMode: EventoModeState }) => boolean
  > = {};

  AVAILABLE_MODES.forEach((mode) => {
    selectors[
      `selectIs${mode.name.charAt(0).toUpperCase() + mode.name.slice(1)}Mode`
    ] = (state: { eventoMode: EventoModeState }) =>
        state.eventoMode.eventoMode === mode.id;
  });

  return selectors;
};

export const modeSelectors = createModeSelectors();

// Constantes exportadas
export { EVENTO_MODE_KEY };

export default eventoModeSlice.reducer;

// Middleware removido - funcionalidade movida para eventoModeMiddleware
